// Tiny in-memory registry bridging DeityInterface (Phase 2 backend design,
// docs/DEITY_AGENT_BACKEND_DESIGN.md §4) to whichever tool panel currently
// has a form open. A tool screen calls `registerDeityForm(formKey, ...)` on
// mount and unregisters on unmount; DeityInterface looks it up by the
// formKey resolved from the active toolId (see TOOL_FORM_KEYS below) to:
//   1. read `getCurrentValues()` into the outgoing `formContext.currentValues`
//   2. route an accepted `proposeFormFill` action's `fields` through
//      `applyFill(fields, recordId)`, which MUST go through the tool's own
//      normal save handler (never a direct supabase write from here).
import type { DmtToolId } from './spectrum-tools';

export interface DeityFormAdapter {
  /** Current in-memory form state to send as formContext.currentValues, or
   *  undefined if no matching form/record is actually open right now. */
  getCurrentValues: () => Record<string, unknown> | undefined;
  /** Applies an accepted deity-proposed fill via the tool's normal save path
   *  (validation, sync columns, optimistic UI) — never a raw supabase write. */
  applyFill: (fields: Record<string, unknown>, recordId?: string) => void;
}

const registry = new Map<string, DeityFormAdapter>();

export function registerDeityForm(formKey: string, adapter: DeityFormAdapter): void {
  registry.set(formKey, adapter);
}

export function unregisterDeityForm(formKey: string): void {
  registry.delete(formKey);
}

export function getDeityForm(formKey: string): DeityFormAdapter | undefined {
  return registry.get(formKey);
}

// toolId (ToolHost screen id, as passed to DeityInterface) -> the single
// formKey that tool's panel may register (V1 ships exactly these three —
// docs/DEITY_AGENT_BACKEND_DESIGN.md §4/§7 task 7).
export const TOOL_FORM_KEYS: Partial<Record<DmtToolId | string, string>> = {
  atlas: 'atlas',
  // `telos` and `oracle` both open AtlasTool (ToolHost), which registers the 'atlas' form.
  oracle: 'atlas',
  tkne: 'design',
  telos: 'atlas',
  jnus: 'jnus_decision',
};

export function formKeyForTool(toolId: string): string | undefined {
  return TOOL_FORM_KEYS[toolId];
}
